/* global Office */

import type { ClientToolExecutorAdapter } from "@magnus/react/copilot-tools";
import { raceWithTimeout } from "@/lib/office/run-executor";
import { formatOfficeError } from "./utils";

// ---------------------------------------------------------------------------
// outlookRun — thin wrapper for item availability + error normalisation
// ---------------------------------------------------------------------------

function outlookRun<T>(
  fn: (item: Office.MessageRead) => Promise<T>
): Promise<T> {
  const item = Office.context?.mailbox?.item;
  if (!item) {
    return Promise.reject(
      new Error(
        "No email is currently open. Open an email in Outlook and try again."
      )
    );
  }
  return fn(item as Office.MessageRead);
}

/**
 * Run against a compose-mode item. The compose-mutation tools require an open
 * composer; `item.subject.setAsync` only exists in compose mode, so a read item
 * fails the cast at runtime — surface a clear message instead.
 *
 * IMPORTANT — two separate, non-chaining bridges. `Office.context.mailbox.item`
 * is whatever surface the task pane was launched against: a read item, or the
 * composer the user *already had open* when they invoked the add-in. The
 * `display*Form` tools open a NEW composer window, but that window does NOT
 * become `mailbox.item` for this pane — so the `set_compose_*` mutators cannot
 * reach a composer that Magnus just opened. The supported flow is: the user
 * opens/starts a compose window themselves, THEN asks Magnus to set the
 * subject/body/recipients on it. To author a fresh message end-to-end, use the
 * server-side Graph draft tools (`create_reply_draft` / `create_draft`) instead.
 */
function composeRun<T>(
  fn: (item: Office.MessageCompose) => Promise<T>
): Promise<T> {
  const item = Office.context?.mailbox?.item as
    | Office.MessageCompose
    | undefined;
  if (!item || typeof item.subject?.setAsync !== "function") {
    return Promise.reject(
      new Error(
        "No compose window is open. Open or start a reply/new email in Outlook first."
      )
    );
  }
  return fn(item);
}

/** Promisify an Office.js `(callback) => void` async op. */
function officeAsync<T>(
  run: (resolve: (r: Office.AsyncResult<T>) => void) => void,
  label: string
): Promise<T> {
  return new Promise<Office.AsyncResult<T>>((resolve) => run(resolve)).then(
    (result) => {
      if (result.status === Office.AsyncResultStatus.Failed) {
        throw new Error(`${label}: ${result.error.message}`);
      }
      return result.value;
    }
  );
}

// ---------------------------------------------------------------------------
// Tool executors
// ---------------------------------------------------------------------------

/**
 * Resolve the Graph-compatible Message ID of the open email. Office.js exposes
 * the EWS-format `itemId`, which Graph rejects — `convertToRestId` translates
 * it to the v2.0 REST id the server-side mail tools expect.
 */
function getCurrentEmailIdCore(): Promise<unknown> {
  return outlookRun((item) => {
    const messageId = Office.context.mailbox.convertToRestId(
      item.itemId,
      Office.MailboxEnums.RestVersion.v2_0
    );
    return Promise.resolve({ messageId });
  });
}

function setComposeSubjectCore(
  args: Record<string, unknown>
): Promise<unknown> {
  // Guard against silently overwriting the user's subject with "" when the
  // model omits or malforms the arg — error instead of clobbering.
  if (typeof args.subject !== "string") {
    return Promise.reject(
      new Error("A subject string is required to set the compose subject.")
    );
  }
  const subject = args.subject;
  return composeRun(async (item) => {
    await officeAsync<void>(
      (cb) => item.subject.setAsync(subject, cb),
      "Failed to set subject"
    );
    return { ok: true, subject };
  });
}

function setComposeBodyCore(args: Record<string, unknown>): Promise<unknown> {
  // 'replace' overwrites the whole body, so a silent "" default would wipe what
  // the user typed when the arg is missing/malformed — error instead.
  if (typeof args.body !== "string") {
    return Promise.reject(
      new Error("Body text is required to set the compose body.")
    );
  }
  const body = args.body;
  const append = args.mode === "append";
  return composeRun(async (item) => {
    const options = { coercionType: Office.CoercionType.Html };
    await officeAsync<void>(
      (cb) =>
        append
          ? item.body.prependAsync(body, options, cb)
          : item.body.setAsync(body, options, cb),
      "Failed to set body"
    );
    return { ok: true, mode: append ? "append" : "replace" };
  });
}

function addComposeRecipientsCore(
  args: Record<string, unknown>
): Promise<unknown> {
  const pick = (key: string): string[] =>
    Array.isArray(args[key])
      ? (args[key] as unknown[]).filter(
          (v): v is string => typeof v === "string"
        )
      : [];
  const to = pick("to");
  const cc = pick("cc");
  const bcc = pick("bcc");
  return composeRun(async (item) => {
    if (to.length) {
      await officeAsync<void>(
        (cb) => item.to.addAsync(to, cb),
        "Failed to add To recipients"
      );
    }
    if (cc.length) {
      await officeAsync<void>(
        (cb) => item.cc.addAsync(cc, cb),
        "Failed to add Cc recipients"
      );
    }
    if (bcc.length) {
      await officeAsync<void>(
        (cb) => item.bcc.addAsync(bcc, cb),
        "Failed to add Bcc recipients"
      );
    }
    return {
      ok: true,
      added: { to: to.length, cc: cc.length, bcc: bcc.length },
    };
  });
}

function addComposeAttachmentCore(
  args: Record<string, unknown>
): Promise<unknown> {
  const url = typeof args.url === "string" ? args.url : "";
  const name = typeof args.name === "string" ? args.name : "attachment";
  if (!url) {
    return Promise.reject(new Error("A file URL is required to attach."));
  }
  return composeRun(async (item) => {
    await officeAsync<string>(
      (cb) => item.addFileAttachmentAsync(url, name, { isInline: false }, cb),
      "Failed to add attachment"
    );
    return { ok: true, name };
  });
}

function saveComposeDraftCore(): Promise<unknown> {
  return composeRun(async (item) => {
    const itemId = await officeAsync<string>(
      (cb) => item.saveAsync(cb),
      "Failed to save draft"
    );
    return { ok: true, itemId };
  });
}

// The synchronous `displayReplyForm` / `displayReplyAllForm` are Mailbox
// requirement set 1.1 (broadly supported, including classic Outlook for Mac);
// the `*Async` variants are 1.9 and unsupported on many Mac builds, where they
// surface as a misleading "insufficient permissions" error. These are
// fire-and-forget (no async result), so we resolve immediately.
function displayReplyFormCore(args: Record<string, unknown>): Promise<unknown> {
  const body = typeof args.body === "string" ? args.body : "";
  return outlookRun((item) => {
    item.displayReplyForm(body);
    return Promise.resolve({ opened: true });
  });
}

function displayReplyAllFormCore(
  args: Record<string, unknown>
): Promise<unknown> {
  const body = typeof args.body === "string" ? args.body : "";
  return outlookRun((item) => {
    item.displayReplyAllForm(body);
    return Promise.resolve({ opened: true });
  });
}

/**
 * Open a new-message composer. Unlike the reply forms this lives on the
 * mailbox (not the open item), so it works even when nothing is open, and
 * `displayNewMessageForm` is fire-and-forget (no async result callback).
 */
function displayNewEmailFormCore(
  args: Record<string, unknown>
): Promise<unknown> {
  const mailbox = Office.context?.mailbox;
  if (!mailbox) {
    return Promise.reject(
      new Error("Outlook is not available — cannot open a new email.")
    );
  }

  const params: {
    toRecipients?: string[];
    subject?: string;
    htmlBody?: string;
  } = {};
  if (Array.isArray(args.to)) {
    params.toRecipients = args.to.filter(
      (r): r is string => typeof r === "string"
    );
  }
  if (typeof args.subject === "string") {
    params.subject = args.subject;
  }
  if (typeof args.body === "string") {
    params.htmlBody = args.body;
  }

  mailbox.displayNewMessageForm(params);
  return Promise.resolve({ opened: true });
}

// ---------------------------------------------------------------------------
// Dispatch
// ---------------------------------------------------------------------------

const CORE_REGISTRY: Record<
  string,
  (args: Record<string, unknown>) => Promise<unknown>
> = {
  get_current_email_id: getCurrentEmailIdCore,
  display_reply_form: displayReplyFormCore,
  display_reply_all_form: displayReplyAllFormCore,
  display_new_email_form: displayNewEmailFormCore,
  set_compose_subject: setComposeSubjectCore,
  set_compose_body: setComposeBodyCore,
  add_compose_recipients: addComposeRecipientsCore,
  add_compose_attachment: addComposeAttachmentCore,
  save_compose_draft: saveComposeDraftCore,
};

const TOOL_TIMEOUTS: Record<string, number> = {
  get_current_email_id: 10_000,
  display_reply_form: 10_000,
  display_reply_all_form: 10_000,
  display_new_email_form: 10_000,
  set_compose_subject: 10_000,
  set_compose_body: 10_000,
  add_compose_recipients: 10_000,
  // Outlook fetches the attachment from the URL — allow headroom.
  add_compose_attachment: 60_000,
  save_compose_draft: 15_000,
};
const DEFAULT_TIMEOUT = 15_000;

export const OUTLOOK_TOOL_NAMES: ReadonlySet<string> = new Set(
  Object.keys(CORE_REGISTRY)
);

function executeOutlookTool(
  toolName: string,
  args: Record<string, unknown>
): Promise<unknown> {
  const coreFn = CORE_REGISTRY[toolName];
  if (!coreFn) {
    return Promise.reject(new Error(`Unknown Outlook tool: ${toolName}`));
  }

  const timeout = TOOL_TIMEOUTS[toolName] ?? DEFAULT_TIMEOUT;

  return raceWithTimeout(
    Promise.resolve(coreFn(args)),
    timeout,
    `Outlook tool "${toolName}" timed out after ${timeout / 1000}s.`
  ).catch((err) => {
    throw new Error(formatOfficeError(err));
  });
}

/**
 * Outlook client-tool adapter. A singleton like `excelClientToolAdapter` /
 * `wordClientToolAdapter` — the only tool that once needed per-chat context
 * (`read_attachment`, which staged bytes into a specific sandbox) moved
 * server-side (ADR-052), so no Outlook client tool is chat-scoped any more.
 */
export const outlookClientToolAdapter: ClientToolExecutorAdapter = {
  execute: (toolName, args) => executeOutlookTool(toolName, args),
  toolNames: OUTLOOK_TOOL_NAMES,
};
